/**
 * Preset threshold formatting (cue-forge-n3e.8). A QC pass is only as credible as
 * the threshold sheet behind it, so the active preset's limits are surfaced in
 * the QC panel and the exported report. Pure and DOM-free: turns a {@link Ruleset}
 * into printable per-rule limit lines and a human verification status, shared by
 * `qc/QcThresholds.tsx` (the read-only sheet in Studio) and `qc/report.ts` (the
 * report header).
 */

import type { RuleConfigMap, RuleId, Ruleset, Severity } from '../../core';
import { RULE_IDS } from '../../core';
import { ruleLabel } from './ruleLabels';

const EM_DASH = '—';

export interface ThresholdLine {
  ruleId: RuleId;
  label: string;
  /** Printable limit, or "—" for a flag-only (parameterless) rule. */
  limit: string;
  severity: Severity;
}

/** Printable threshold for a rule, or "" when the rule has no numeric limit. */
export function thresholdText(id: RuleId, cfg: RuleConfigMap[RuleId]): string {
  switch (id) {
    case 'max-chars-per-line':
      return `${(cfg as RuleConfigMap['max-chars-per-line']).max} chars/line`;
    case 'max-lines-per-cue':
      return `${(cfg as RuleConfigMap['max-lines-per-cue']).max} lines/cue`;
    case 'max-cps':
      return `${(cfg as RuleConfigMap['max-cps']).max} cps`;
    case 'arabic-line-length':
      return `${(cfg as RuleConfigMap['arabic-line-length']).max} chars/line`;
    case 'min-duration':
      return `min ${(cfg as RuleConfigMap['min-duration']).ms} ms`;
    case 'max-duration':
      return `max ${(cfg as RuleConfigMap['max-duration']).ms} ms`;
    case 'min-gap': {
      const g = cfg as RuleConfigMap['min-gap'];
      const parts: string[] = [];
      if (g.frames != null) parts.push(`${g.frames} frames`);
      if (g.ms != null) parts.push(`${g.ms} ms`);
      return parts.join(' / ');
    }
    case 'forbidden-characters': {
      const f = cfg as RuleConfigMap['forbidden-characters'];
      return [...(f.chars ?? []), ...(f.patterns ?? [])].map((c) => JSON.stringify(c)).join(', ');
    }
    case 'mixed-digit-style':
      return (cfg as RuleConfigMap['mixed-digit-style']).style ?? 'western';
    default:
      return '';
  }
}

/** Enabled rules of the ruleset, in catalog order, as printable threshold lines. */
export function thresholdLines(ruleset: Ruleset): ThresholdLine[] {
  const out: ThresholdLine[] = [];
  for (const id of RULE_IDS) {
    const cfg = ruleset.rules[id];
    if (!cfg || cfg.enabled === false) continue;
    const limit = thresholdText(id, cfg);
    out.push({ ruleId: id, label: ruleLabel(id), limit: limit || EM_DASH, severity: cfg.severity });
  }
  return out;
}

export interface VerificationStatus {
  /** Whether the preset's thresholds were confirmed against the cited platform spec. */
  verified: boolean;
  /** The documentation URL the values were (or are to be) checked against. */
  reference?: string;
  /** Short human label: "Verified" | "Draft". */
  label: string;
  /** One-line explanation for a caption / tooltip. */
  caption: string;
}

/**
 * The preset's verification standing, in human terms — so the QC panel and the
 * billable report tell the operator honestly whether the threshold sheet was
 * confirmed against the cited platform spec or is still an engineering draft.
 */
export function verificationStatus(ruleset: Ruleset): VerificationStatus {
  return ruleset.verified
    ? {
        verified: true,
        reference: ruleset.reference,
        label: 'Verified',
        caption: 'Thresholds verified against the cited platform specification.',
      }
    : {
        verified: false,
        reference: ruleset.reference,
        label: 'Draft',
        // A preset with a reference is working toward verifying against it; one
        // without (our own baseline) has no external platform spec to cite.
        caption: ruleset.reference
          ? 'Draft — thresholds not yet verified against the cited reference.'
          : 'Draft — baseline defaults, not tied to a platform specification.',
      };
}
